import { isTouchDevice } from '../is-touch-device';

const hoverEventCleanupMap = new WeakMap<HTMLElement, Map<string, WeakMap<HTMLElement, () => void>>>();
const hoveredClassName = 'et_multi_view__hovered';

/**
 * This function adds event listeners for hover and touch events to switch between default and hover
 * states of an element.
 *
 * @since ??
 *
 * @param {HTMLElement} element The HTML element that the hover effect is being applied to.
 * @param {HTMLElement} hoverElement The HTML element that triggers the hover effect.
 * @param {CallableFunction} switchDataToDefaultState Function that is called when the
 * mouse or touch event leaves the `hoverElement` element. It takes an optional `event` parameter of
 * type `MouseEvent` or `TouchEvent`.
 * @param {CallableFunction} switchDataToHoverState Function that is called when the
 * `hoverElement` is hovered over or touched on a touch device. It is responsible for switching the
 * data or state of the element to its hover state. The function takes an optional `event` parameter,
 * which can be either.
 */
export const addEventListenerHover = (
  element: HTMLElement,
  hoverElement: HTMLElement,
  switchDataToDefaultState: (event?: MouseEvent | TouchEvent) => void,
  switchDataToHoverState: (event?: MouseEvent | TouchEvent) => void,
  listenerNamespace = 'default',
): void => {
  const cleanupByNamespace =
    hoverEventCleanupMap.get(hoverElement) ?? new Map<string, WeakMap<HTMLElement, () => void>>();
  const cleanupByElement = cleanupByNamespace.get(listenerNamespace) ?? new WeakMap<HTMLElement, () => void>();
  const existingCleanup = cleanupByElement.get(element);

  if (existingCleanup) {
    existingCleanup();
  }

  let onMouseLeaveHandler: ((onMouseLeaveEvent: MouseEvent) => void) | null = null;
  let onTouchEndHandler: ((onTouchEndEvent: TouchEvent) => void) | null = null;
  let onDocumentTouchStartHandler: ((onDocumentTouchStartEvent: TouchEvent) => void) | null = null;
  let onTouchStartHandler: (onTouchStartEvent: TouchEvent) => void = () => {};

  const isLinkTarget = (eventTarget: EventTarget | null): eventTarget is Element =>
    eventTarget instanceof Element && (eventTarget.matches('a') || null !== eventTarget.closest('a'));

  const detachDocumentTouchStartHandler = () => {
    if (onDocumentTouchStartHandler) {
      document.removeEventListener('touchstart', onDocumentTouchStartHandler, true);
      onDocumentTouchStartHandler = null;
    }
  };

  const resetTouchState = (touchEvent?: TouchEvent) => {
    switchDataToDefaultState(touchEvent);
    hoverElement.classList.remove(hoveredClassName);

    if (onTouchEndHandler) {
      hoverElement.removeEventListener('touchstart', onTouchEndHandler);
      onTouchEndHandler = null;
    }

    hoverElement.addEventListener('touchstart', onTouchStartHandler, { once: true });
    detachDocumentTouchStartHandler();
  };

  const onMouseEnterHandler = (onMouseEnterEvent: MouseEvent) => {
    if (isTouchDevice()) {
      hoverElement.removeEventListener('mouseenter', onMouseEnterHandler);
      return;
    }

    onMouseLeaveHandler = (onMouseLeaveEvent: MouseEvent) => {
      if (isTouchDevice()) {
        if (onMouseLeaveHandler) {
          hoverElement.removeEventListener('mouseleave', onMouseLeaveHandler);
        }
        return;
      }
      hoverElement.addEventListener('mouseenter', onMouseEnterHandler, { once: true });
      switchDataToDefaultState(onMouseLeaveEvent);
    };

    hoverElement.addEventListener('mouseleave', onMouseLeaveHandler, { once: true });
    switchDataToHoverState(onMouseEnterEvent);
  };

  onTouchStartHandler = (onTouchStartEvent: TouchEvent) => {
    const registerDocumentTouchStartHandler = () => {
      detachDocumentTouchStartHandler();
      onDocumentTouchStartHandler = (onDocumentTouchStartEvent: TouchEvent) => {
        const touchedOutsideHoverElement =
          onDocumentTouchStartEvent.target instanceof Node && !hoverElement.contains(onDocumentTouchStartEvent.target);

        if (touchedOutsideHoverElement) {
          resetTouchState(onDocumentTouchStartEvent);
        }
      };
      document.addEventListener('touchstart', onDocumentTouchStartHandler, {
        once: true,
        capture: true,
      });
    };

    onTouchEndHandler = (onTouchEndEvent: TouchEvent) => {
      // Bail early if touched element is a link or child element of link.
      if (isLinkTarget(onTouchEndEvent.target)) {
        hoverElement.addEventListener('touchstart', onTouchStartHandler, { once: true });
        registerDocumentTouchStartHandler();
        return;
      }

      resetTouchState(onTouchEndEvent);
    };

    hoverElement.addEventListener('touchstart', onTouchEndHandler, { once: true });

    // Bail early if touched element is a link or child element of link.
    if (isLinkTarget(onTouchStartEvent.target)) {
      return;
    }

    const existingHoveredElement = document.querySelector(`.${hoveredClassName}`);

    if (existingHoveredElement && existingHoveredElement !== hoverElement) {
      existingHoveredElement.dispatchEvent(new Event('touchstart'));
    }

    switchDataToHoverState(onTouchStartEvent);
    hoverElement.classList.add(hoveredClassName);
    registerDocumentTouchStartHandler();
  };

  if (isTouchDevice()) {
    hoverElement.addEventListener('touchstart', onTouchStartHandler, { once: true });
  } else {
    hoverElement.addEventListener('mouseenter', onMouseEnterHandler, { once: true });
  }

  cleanupByElement.set(element, () => {
    hoverElement.removeEventListener('mouseenter', onMouseEnterHandler);

    if (onMouseLeaveHandler) {
      hoverElement.removeEventListener('mouseleave', onMouseLeaveHandler);
    }

    hoverElement.removeEventListener('touchstart', onTouchStartHandler);

    if (onTouchEndHandler) {
      hoverElement.removeEventListener('touchstart', onTouchEndHandler);
    }

    detachDocumentTouchStartHandler();
  });

  cleanupByNamespace.set(listenerNamespace, cleanupByElement);
  hoverEventCleanupMap.set(hoverElement, cleanupByNamespace);
};
